import IsAxiosError from 'components/common/IsAxiosError';
import Loading from 'components/common/Loading';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import HubertLabel from 'components/HubertUI/HubertLabel';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useFindTemplatesQuery } from 'rtk/query/apis/templates';
import { useVisibilityLabels } from 'utils/visibilityLabels';

interface Props extends ComponentProps<'div'> {
  clientId?: string;
  isMulti?: boolean;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  visibilityFilter?: string[];
  allowEmptyClientId?: boolean;
}

const ClientTemplateCombobox = ({
  clientId,
  isMulti = false,
  onValueChange,
  value,
  disabled = false,
  triggerClassName,
  contentClassName,
  visibilityFilter,
  allowEmptyClientId = false,
  ...props
}: Props) => {
  const [items, setItems] = useState<{ label: string; value: string }[]>([]);
  const { visibilityLabel } = useVisibilityLabels();
  const shouldSkip = !clientId && !allowEmptyClientId;
  const { data, isFetching, isLoading, isError, error } = useFindTemplatesQuery(
    { filter: { clientId: clientId || '', context: 'current', name: '', visibility: visibilityFilter || [] } },
    { skip: shouldSkip },
  );

  useEffect(() => {
    if (data?.templates) {
      const sortedItems = [...data.templates].sort((a, b) => {
        const visibilityOrder = ['-', 'global', 'client', 'local'];
        const aIndex = visibilityOrder.indexOf(a.visibility || '-');
        const bIndex = visibilityOrder.indexOf(b.visibility || '-');
        return aIndex - bIndex;
      });
      setItems(
        sortedItems.map((x) => ({
          label: `${x.name} (${x.visibility ? visibilityLabel(x.visibility) : '-'})`,
          value: x._id,
        })),
      );
    }
  }, [data?.templates, visibilityLabel]);

  if (!clientId && !allowEmptyClientId) {
    return null;
  }

  if (isLoading || isFetching) {
    return <Loading label="languages" />;
  }

  if (isError) {
    return <IsAxiosError error={error} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div {...props}>
      <HubertCombobox
        itemName="client template"
        isMulti={isMulti}
        items={items}
        onValueChange={onValueChange}
        value={value}
        disabled={disabled}
        triggerClassName={triggerClassName}
        contentClassName={contentClassName}
        portalContent
        defaultSort={false}
      />
      <HubertLabel className="text-danger-500 text-xs">Note: only current versions available here.</HubertLabel>
    </div>
  );
};

export default ClientTemplateCombobox;
